import { IonIcon, IonInput, IonItem, IonLabel, IonRow, IonSkeletonText, IonSpinner, IonText } from '@ionic/react';
import { caretDownOutline } from 'ionicons/icons';
import { ReactNode, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { WrapperDiv } from './FormSelectable.styles';
import FormSelectableModal from './FormSelectableModal';

interface IComponentProps<T> {
  control: Control<any, object>;
  name: string;

  label?: string;
  placeholder?: string;

  fieldKey?: string;
  itemKey: string;
  itemValue: string;
  itemValueSub?: string;
  items: T[];

  itemsLoading?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  icon?: string;
  iconColor?: string;
  showLoading?: boolean;
  allowSearch?: boolean;
  flexFill?: boolean;
  hasLine?: boolean;

  itemDisabled?: (item: T) => boolean;

  handleClick?: () => void;
  handleSelection?: (value: T) => void;
  handleSearch?: (arg: string) => void;
  handleDismiss?: () => void;
}

// You are either mapping your form value to an object or a single value. Eg. the form value that matches NAME could be either:
// A) item: { code:string, description:string } or
// B) item: string

// items<any[]>: items list
// name<string>: the NAME of the form field to map to
// fieldKey<string>: when mapping NAME to an object as in (A) above, this defines the key value in that object. It can be different to itemKey. NOTE: do not use this if mapping to a non-object (B) above.
// itemKey<string>: unique key from the items list
// itemValue<string>: display value from the items list will the the value that is displayed in the list
// itemValueSub<string>: sub-display value from the items list (displays underneath the display value)
const FormSelectable = <T extends object>({
  control,
  name,
  label,
  placeholder,
  fieldKey,
  itemKey,
  itemValue,
  itemValueSub,
  items,
  itemsLoading,
  isLoading,
  icon,
  iconColor,
  showLoading,
  allowSearch,
  flexFill,
  hasLine,
  itemDisabled,
  handleClick,
  handleSelection,
  handleSearch,
  handleDismiss,
}: IComponentProps<T> & { children?: ReactNode }) => {
  const [showModal, setShowModal] = useState(false);
  const fieldKeyClone = fieldKey || 'code';

  const handleInputClick = () => {
    //if (!disabled) {
    handleClick && handleClick();
    setShowModal(true);
    //}
  };

  const getMatchingItem = (fieldValue: any) => {
    const fieldKeyValue = fieldValue[fieldKeyClone] as string | boolean | number;
    if ((fieldValue || fieldKeyValue === false || fieldKeyValue === 0) && items?.length) {
      const matchingItem = items.find((item) => {
        return (item as any)[itemKey] === fieldKeyValue;
      });
      return matchingItem || {};
    }
    return {};
  };

  const getFieldDisplay = (fieldValue: any) => {
    if (fieldValue !== undefined && fieldValue !== null) {
      const matchingItem = getMatchingItem(fieldValue);
      return (matchingItem as any)[itemValue]?.toString() || fieldValue[fieldKeyClone];
    }
    return '';
  };

  return (
    <Controller
      control={control}
      name={name || ''}
      render={({ field, fieldState: { error } }) => {
        let fieldClone = { ...field };
        if (field.value) {
          if (!fieldKey) {
            fieldClone.value = {
              [fieldKeyClone]: field.value[fieldKeyClone] || field.value,
            };
          }
        }

        return (
          <WrapperDiv>
            <IonRow className="form-row">
              {isLoading ? (
                <IonItem className={`ion-no-padding ${flexFill || flexFill === undefined ? 'flexed-fill' : ''}`}>
                  <IonLabel position="stacked">{label}</IonLabel>
                  <IonSkeletonText animated />
                  {error && <IonText className="error">{error.message}</IonText>}
                </IonItem>
              ) : (
                <div className="flexed-fill">
                  <IonItem onClick={() => handleInputClick()} className={flexFill || flexFill === undefined ? 'ion-no-padding flexed-fill test' : 'ion-no-padding test'} lines={hasLine === undefined ? 'full' : hasLine ? 'full' : 'none'}>
                    <IonInput placeholder={placeholder} readonly value={getFieldDisplay(fieldClone.value)} label={label} />
                    {itemsLoading && <IonSpinner slot="end" name="lines-small" />}
                    <IonIcon className={`${icon ? 'padded-icon' : ''}`} icon={caretDownOutline} slot="end" color={iconColor || 'medium'} />
                    {icon && <IonIcon icon={icon} size="small" className="field-icon" color={iconColor || 'medium'} />}
                  </IonItem>
                  {error && <IonText className="error">{error.message}</IonText>}
                </div>
              )}
            </IonRow>

            <FormSelectableModal
              field={fieldClone}
              fieldKey={fieldKey}
              itemKey={itemKey}
              itemValue={itemValue}
              itemValueSub={itemValueSub}
              placeholder={placeholder}
              allowSearch={allowSearch}
              handleDismiss={() => {
                setShowModal(false);
                handleDismiss && handleDismiss();
              }}
              handleSearch={handleSearch}
              handleSelection={(val) => {
                setShowModal(false);
                handleSelection && handleSelection(val);
              }}
              itemDisabled={itemDisabled}
              items={items}
              showLoading={showLoading}
              showModal={showModal}
            />
          </WrapperDiv>
        );
      }}
    />
  );
};

export default FormSelectable;
